import * as inflection from 'inflection';
import * as _ from 'lodash-es';
import React from 'react';
import type { Tagged } from 'type-fest';
import type {
  ToastMessage,
  ToastProps,
  ToastPunctuation,
} from '../components/Toast';
import { TOAST_PUNCTUATION } from '../components/Toast';
import { PostType, VerificationUnit } from '../graphql/generated/graphql';
import templatePrefilledIcon from '../icons/prefilled/16x16/template.svg';
import { PostIconForState } from '../post/PostIcon';
import VerifiedIcon from '../post/VerifiedIcon';
import { humanReadableFrequency } from '../post/verificationDateHelpers';
import getFeature from '../utils/features';
import ContactSupportButton from './ContactSupportButton';
import UploadRestrictionUpgradeButton from './UploadRestrictionUpgradeButton';

export type TaggedToastProps = Tagged<ToastProps, 'toast'>;
type ToastNamespaceLabel = string;

type ToastTemplate<T> = {
  type: 'template';
  toProps: (args: T) => TaggedToastProps;
  // Loosing the type information for exampleData is okay, as this is only used in Storybook.
  // It allows us to use `never` instead of `any` for the dynamic template creation.
  exampleData: unknown;
};

type Toast = {
  type: 'toast';
  props: TaggedToastProps;
};

const tagToastProps = (props: ToastProps) => props as TaggedToastProps;

const toast = (props: ToastProps): Toast => ({
  type: 'toast',
  props: tagToastProps(props),
});

const template = <Args,>(
  argsToProps: (args: Args) => ToastProps,
  exampleData: Args,
): ToastTemplate<Args> => ({
  type: 'template',
  toProps: (args: Args) => tagToastProps(argsToProps(args)),
  exampleData,
});

type ToastMap = Record<string, Toast | ToastTemplate<never>>;

// eslint-disable-next-line import/no-unused-modules -- exposed for testing
export const toastRegistry: Record<ToastNamespaceLabel, ToastMap> = {};

// This just turns the ToastMap into a map that points either to the tagged props or the template function
// so that it can be conveniently used with `UIActions.pushToast`, .e.g
// ```
// const foo = toastNamespace('Foo', {
//   bar: toast({ message: 'Bar' }),
//   baz: template(({ message }) => ({ message }), { message: 'Baz' }),
// });
// ```
// can be used with `UIActions.pushToast` like this:
// ```
// UIActions.pushToast(foo.bar);
// UIActions.pushToast(foo.baz({ message: 'Baz' }));
// ```
type ToastTemplateOrPropsMap<Map extends ToastMap> = {
  [Key in keyof Map]: Map[Key] extends { type: 'toast' }
    ? Map[Key]['props']
    : Map[Key] extends { type: 'template' }
      ? Map[Key]['toProps']
      : never;
};

const toastNamespace = <Map extends ToastMap>(
  namespaceLabel: ToastNamespaceLabel,
  toastMap: Map,
): ToastTemplateOrPropsMap<Map> => {
  // register the toastMap for Storybook
  toastRegistry[namespaceLabel] = toastMap;

  return Object.fromEntries(
    Object.entries(toastMap).map(([key, value]) => [
      key,
      value.type === 'toast' ? value.props : value.toProps,
    ]),
  ) as ToastTemplateOrPropsMap<Map>;
};

const addPunctuationIfNeeded = (message: string): ToastMessage => {
  const lastCharacter = message.at(-1);

  const hasPunctuation =
    lastCharacter &&
    TOAST_PUNCTUATION.includes(lastCharacter as ToastPunctuation);

  return hasPunctuation ? (message as ToastMessage) : `${message}.`;
};

export const invitationToasts = toastNamespace('Invitation', {
  signupLinkCopied: toast({
    level: 'success',
    message: 'Signup link copied.',
  }),
  alreadyAcceptedError: toast({
    level: 'error',
    message: 'Invitation already accepted. Please login normally.',
  }),
  invitationNotFoundError: toast({
    level: 'error',
    title: 'We could not find your invitation',
    message:
      'Please check the URL or ask your teammate to re-send an invitation.',
  }),
  singleSignOnWarning: template(
    ({ organizationName }) => ({
      level: 'warning',
      message: `Please use Single Sign-On to join ${organizationName}.`,
    }),
    { organizationName: 'Example Organization' },
  ),
  invitationSent: template(
    ({ totalSent }) => ({
      level: 'success',
      message: `Invitations sent to ${totalSent} ${inflection.inflect(
        'teammate',
        totalSent,
      )}.`,
    }),
    { totalSent: 2 },
  ),
  inviteResent: toast({
    level: 'success',
    message: 'Invite resent.',
  }),
  invitationApproved: template(
    ({ userName }) => ({
      level: 'success',
      message: `${userName}'s invitation has been approved.`,
    }),
    { userName: 'Jon' },
  ),
  invitationRejected: template(
    ({ userName }) => ({
      level: 'success',
      message: `${userName}'s invitation has been rejected.`,
    }),
    { userName: 'Jon' },
  ),
  userExistsWarning: template(
    ({ label }) => ({
      level: 'warning',
      message: `${label} is already on your team.`,
    }),
    { label: 'Jon' },
  ),
  userInvitedWarning: template(
    ({ label }) => ({
      level: 'warning',
      message: `${label} is already invited.`,
    }),
    { label: 'Jon' },
  ),
  rateLimitError: template(
    ({ label }) => ({
      level: 'error',
      title: 'Too many invites',
      message: `You sent too many invites. ${label} has not been invited!`,
    }),
    { label: 'Jon' },
  ),
  invitationRequestSent: template(
    ({ totalSent }) => ({
      level: 'success',
      message: `${totalSent} ${inflection.inflect(
        'invitation request',
        totalSent,
      )} sent to your team admins.`,
    }),
    {
      totalSent: 2,
    },
  ),
});

export const userToasts = toastNamespace('User', {
  emailUpdated: toast({
    level: 'success',
    title: 'Email updated',
    message: 'Your email has been verified and updated successfully.',
  }),
  emailUpdateFailedError: toast({
    level: 'error',
    title: 'Email update failed',
    message:
      'Something went wrong while updating your email. Please try again later.',
  }),
  verifyYourEmailWarning: toast({
    level: 'warning',
    title: 'Verify your email',
    message:
      'Please check your email inbox to complete the update to your new address.',
  }),
});

export const postToasts = toastNamespace('Post', {
  noPostPermissionError: template(
    ({ onRequestAccess }) => ({
      level: 'error',
      title: 'No permission to view this post',
      message:
        'You do not have permission to view this post. Request access from the owner?',
      button: {
        text: 'Request access',
        onClick: onRequestAccess,
      },
    }),
    { onRequestAccess: () => Promise.resolve() },
  ),
  accessRequested: toast({
    level: 'success',
    message: 'Access request submitted.',
  }),
  postDeletedError: toast({
    level: 'error',
    message: 'The post you were viewing was just deleted.',
    title: 'Post deleted',
  }),
  threadNotFoundError: toast({
    level: 'error',
    title: 'Thread not found',
    message: 'We could not find the requested thread.',
  }),
  alreadyVerified: template(
    ({ verifierName }) => ({
      level: 'success',
      title: 'Post already verified',
      message: `${verifierName} has already verified this post!`,
    }),
    { verifierName: 'Jon Snow' },
  ),
  postShared: template(
    ({ onViewSharing }) => ({
      level: 'success',
      title: 'Post shared',
      message: 'The post has been shared with the selected user.',
      button: {
        text: 'View sharing',
        onClick: onViewSharing,
      },
    }),
    {
      onViewSharing: () => {},
    },
  ),
  postSharedWithGroup: template(
    ({ onViewSharing }) => ({
      level: 'success',
      title: 'Post shared',
      message: 'The post has been shared with the selected group.',
      button: {
        text: 'View sharing',
        onClick: onViewSharing,
      },
    }),
    {
      onViewSharing: () => {},
    },
  ),
  convertedIntoTemplate: toast({
    level: 'success',
    title: 'Converted into template',
    message:
      "You can now use this template to create brand new posts as a copy of this template's contents.",
    icon: templatePrefilledIcon,
  }),
  embedCodeCopied: toast({
    level: 'success',
    title: 'Embed code copied',
    message: 'Paste anywhere that supports iframe embeds.',
  }),
  postExportError: toast({
    level: 'error',
    title: 'Unable to export the post',
    message:
      'We could not export this post. Please contact support for assistance.',
    button: ({ onDismiss }) => <ContactSupportButton onDismiss={onDismiss} />,
  }),
  postRestored: toast({
    level: 'success',
    message: 'Your post has been restored!',
  }),
  updateRejectedByServerError: toast({
    level: 'error',
    title: 'Update rejected by server',
    message: 'Please save your most recent changes and refresh the page.',
  }),
  saveWarning: toast({
    level: 'warning',
    message: 'Still trying to save…',
  }),
  offlineError: toast({
    level: 'error',
    title: 'You are offline',
    message: 'Please save your work and reconnect.',
  }),
  saved: toast({
    level: 'success',
    message: 'Saved!',
  }),
  verified: template(
    ({ unit, quantity }) => ({
      level: 'success',
      icon: <VerifiedIcon />,
      title: 'This post is verified',
      message: `You'll be asked to verify again in ${humanReadableFrequency({
        unit,
        quantity,
      })}.`,
    }),
    {
      quantity: 1,
      unit: VerificationUnit.Month,
    },
  ),
  postArchived: template(
    ({ isDraft, onUndo }) => ({
      title: 'Post moved to archive',
      icon: (
        <PostIconForState
          state={{
            type: PostType.Post,
            draft: isDraft,
            archived: true,
            public: false,
          }}
        />
      ),
      message:
        'This post has been archived. You can always restore it later from the Archive.',
      button: {
        text: 'Undo',
        onClick: onUndo,
      },
    }),
    {
      onUndo: () => {},
      isDraft: false,
    },
  ),
  postUnarchived: template(
    ({ isDraft, onUndo }) => ({
      title: 'Post unarchived',
      message:
        'This post has been unarchived. You can move it back to the Archive anytime.',
      button: {
        text: 'Undo',
        onClick: onUndo,
      },
      icon: (
        <PostIconForState
          state={{
            type: PostType.Post,
            draft: isDraft,
            archived: false,
            public: false,
          }}
        />
      ),
    }),
    { isDraft: false, onUndo: () => {} },
  ),
  postPublished: template(
    ({ onAddTopics }) => ({
      message:
        'Your teammates can now access and search for this post. Add a topic to help them more easily discover it.',
      title: 'Post published without topics',
      button: {
        text: 'Add topics',
        onClick: onAddTopics,
      },
    }),
    {
      onAddTopics: () => {},
    },
  ),
  threadResolved: template(
    ({ onUndo }) => ({
      level: 'success',
      title: 'Thread resolved',
      button: { text: 'Undo', onClick: onUndo },
    }),
    { onUndo: () => {} },
  ),
  threadUnresolved: template(
    ({ onUndo }) => ({
      level: 'success',
      title: 'Thread unresolved',
      button: { text: 'Undo', onClick: onUndo },
    }),
    { onUndo: () => {} },
  ),
  threadUnresolveError: toast({
    level: 'error',
    message:
      'Could not unresolve comment because the commented text has been deleted from the post.',
  }),
  ownerChanged: template(
    ({ onUndo }) => ({
      icon: <VerifiedIcon />,
      title: 'The owner has been changed',
      message:
        "They're responsible for keeping verified posts up-to-date and reverifying them regularly.",
      button: {
        text: 'Undo',
        onClick: onUndo,
      },
    }),
    {
      onUndo: () => {},
    },
  ),
  postOnlyAccessibleByTopicMembersSecret: template(
    ({ onUndo }) => ({
      title: "This post is now only accessible by its topic's members",
      message: 'Secret topics are only accessible by their members.',
      button: {
        text: 'Undo',
        onClick: onUndo,
      },
    }),
    {
      onUndo: () => {},
    },
  ),
  postOnlyAccessibleByTopicMembersPrivate: template(
    ({ onUndo }) => ({
      title: "This post is now only accessible by its topic's members",
      message: 'Private topics are only accessible by their members.',
      button: {
        text: 'Undo',
        onClick: onUndo,
      },
    }),
    {
      onUndo: () => {},
    },
  ),
  postOnlyAccessibleByTopicMembersPrivateAndSecret: template(
    ({ onUndo }) => ({
      title: "This post is now only accessible by its topic's members",
      message:
        'Posts with both private and secret topics are accessible by members of either topic.',
      button: {
        text: 'Undo',
        onClick: onUndo,
      },
    }),
    {
      onUndo: () => {},
    },
  ),
  postRemainsAccessibleByAnyoneOpenAndSecret: template(
    ({ organizationName }) => ({
      title: `This post will remain accessible by anyone at ${organizationName}`,
      message:
        'Posts with both open and secret topics are accessible by anyone.',
      button: {
        text: 'Got it',
      },
    }),
    {
      organizationName: 'Organization 1',
    },
  ),
  postRemainsAccessibleByAnyoneOpenAndPrivate: template(
    ({ organizationName }) => ({
      title: `This post will remain accessible by anyone at ${organizationName}`,
      message:
        'Posts with both open and private topics are accessible by anyone.',
      button: {
        text: 'Got it',
      },
    }),
    {
      organizationName: 'Organization 1',
    },
  ),
  postRemainsAccessibleByAnyoneOpenSecretAndPrivate: template(
    ({ organizationName }) => ({
      title: `This post will remain accessible by anyone at ${organizationName}`,
      message:
        'Posts with open, secret and private topics are accessible by anyone.',
      button: {
        text: 'Got it',
      },
    }),
    {
      organizationName: 'Organization 1',
    },
  ),
  postNowAccessibleByAnyone: template(
    ({ organizationName, onUndo }) => ({
      title: `This post is now accessible by anyone at ${organizationName}`,
      message: 'Posts with no topics are accessible by anyone.',
      button: {
        text: 'Undo',
        onClick: onUndo,
      },
    }),
    {
      organizationName: 'Organization 1',
      onUndo: () => {},
    },
  ),
  postNowAccessibleByAnyoneOpenAndSecret: template(
    ({ organizationName, onUndo }) => ({
      title: `This post is now accessible by anyone at ${organizationName}`,
      message:
        'Posts with both open and secret topics are accessible by anyone.',
      button: {
        text: 'Undo',
        onClick: onUndo,
      },
    }),
    {
      organizationName: 'Organization 1',
      onUndo: () => {},
    },
  ),
  postNowAccessibleByAnyoneOpenAndPrivate: template(
    ({ organizationName, onUndo }) => ({
      title: `This post is now accessible by anyone at ${organizationName}`,
      message:
        'Posts with both open and private topics are accessible by anyone.',
      button: {
        text: 'Undo',
        onClick: onUndo,
      },
    }),
    {
      organizationName: 'Organization 1',
      onUndo: () => {},
    },
  ),
  postNowAccessibleByAnyoneOpenSecretAndPrivate: template(
    ({ organizationName, onUndo }) => ({
      title: `This post is now accessible by anyone at ${organizationName}`,
      message:
        'Posts with open, secret and private topics are accessible by anyone.',
      button: {
        text: 'Undo',
        onClick: onUndo,
      },
    }),
    {
      organizationName: 'Organization 1',
      onUndo: () => {},
    },
  ),
  postRemainsAccessibleByTopicMembersPrivateAndSecret: toast({
    title: "This post will remain accessible by its topic's members",
    message:
      'Posts with both private and secret topics are accessible by members of either topic.',
    button: {
      text: 'Got it',
    },
  }),
  postRemainsAccessibleByTopicMembersPrivate: toast({
    title: "This post will remain accessible by its topic's members",
    message: 'Private topics are only accessible by their members.',
    button: {
      text: 'Got it',
    },
  }),
  postRemainsAccessibleByTopicMembersSecret: toast({
    title: "This post will remain accessible by its topic's members",
    message: 'Secret topics are only accessible by their members.',
    button: {
      text: 'Got it',
    },
  }),
});

export const editorToasts = toastNamespace('Editor', {
  codeCopied: toast({
    level: 'success',
    message: 'Code copied.',
  }),
  markdownCopied: toast({
    level: 'success',
    message: 'Markdown copied.',
  }),
  markdownCopyError: toast({
    level: 'error',
    title: 'Unable to copy markdown',
    message: 'Please keep the page focused while processing and try again.',
  }),
  downloadAttachmentsError: toast({
    level: 'error',
    title: 'Failed to download attachments',
    message: 'An error occurred while downloading the attachments.',
  }),
  imageCopyError: toast({
    level: 'error',
    message:
      'We could not make a copy of the pasted image and it may be inaccessible to others in the future.',
  }),
  overlargeFileError: template(
    ({ fileName, maxSizeMB }) => ({
      level: 'error',
      title: 'File too large',
      message: `${fileName} failed to upload. It exceeded the maximum upload size of ${maxSizeMB}MB.`,
    }),
    {
      fileName: 'file.txt',
      maxSizeMB: 25,
    },
  ),
  genericImageUploadError: toast({
    level: 'error',
    message: 'Image could not be uploaded.',
  }),
  msWordWarning: toast({
    level: 'warning',
    message:
      'Pasting MS Word content with images is not supported. Upload each image separately.',
  }),
  attachmentSizeExceededError: toast({
    level: 'warning',
    title: 'Attachment too large',
    hideIcon: true,
    message: `Your team's maximum upload size is ${getFeature('attachment_size_limit')} MB. Upgrade to increase your limit.`,
    button: ({ onDismiss }) => (
      <UploadRestrictionUpgradeButton onClick={onDismiss} />
    ),
  }),
  imageSizeExceededError: toast({
    level: 'warning',
    title: 'Image too large',
    hideIcon: true,
    message: `Your team's maximum upload size is ${getFeature('attachment_size_limit')} MB. Upgrade to increase your limit.`,
    button: ({ onDismiss }) => (
      <UploadRestrictionUpgradeButton onClick={onDismiss} />
    ),
  }),
  attachmentUploadError: toast({
    level: 'error',
    message: 'Attachment could not be uploaded.',
  }),
  editorLockedError: toast({
    level: 'error',
    title: 'Editor is locked',
    message:
      'Editor is locked due to an encountered error. Please refresh the page to retry.',
  }),
  couldNotConnectForEditingError: toast({
    level: 'error',
    title: 'Could not connect for editing',
    message: 'Please refresh the page to reconnect.',
  }),
  buttonActionTriggered: toast({
    level: 'success',
    message: 'Action successfully triggered.',
  }),
  buttonActionTriggeredError: toast({
    level: 'error',
    message: 'There was an error triggering the action.',
  }),
  buttonActionInvalidUrlError: toast({
    level: 'error',
    message:
      'There was an error triggering the action: The URL of the action is invalid.',
  }),
  buttonActionStatusError: template(
    ({ status }) => ({
      level: 'error',
      message: `Action triggered a ${status} response.`,
    }),
    {
      status: 500,
    },
  ),
  carouselImagesNotSupportedWarning: toast({
    level: 'warning',
    title: 'Unsupported files in carouse',
    message: "Some of these files aren't supported in the image carousel.",
    button: {
      text: 'See supported formats',
      to: 'https://help.slab.com/en/articles/3805244-using-images-in-posts#h_767d0bc9d9',
      newTab: true,
    },
  }),
});

export const topicToasts = toastNamespace('Topic', {
  publicTopicLinkCopied: toast({
    level: 'success',
    message: 'Public topic link copied.',
  }),
  membershipRequested: toast({
    level: 'success',
    title: 'Membership requested',
    message: 'You will receive an email when access is granted.',
  }),
  memberAdded: template(
    ({ userName }) => ({
      level: 'success',
      title: 'New topic member added',
      message: `${userName} has been added as a member of this topic.`,
    }),
    {
      userName: 'Jon',
    },
  ),
  memberRemovedWarning: template(
    ({ groupName, topicName }) => ({
      level: 'warning',
      title: `${groupName} has been removed as a member`,
      message: `Some ${groupName} members may still be members of ${topicName} directly or through a group.`,
    }),
    {
      groupName: 'Group 1',
      topicName: 'Topic 1',
    },
  ),
  requestRejectedWarning: toast({
    level: 'warning',
    title: 'Topic member not added',
    message: 'Request has been rejected by another member of this topic.',
  }),
  requestDeclinedWarning: template(
    ({ userName }) => ({
      level: 'warning',
      title: 'Membership request declined',
      message: `${userName}'s request has been removed.`,
    }),
    {
      userName: 'Jon',
    },
  ),
  noLongerInherited: template(
    ({ inheritedParentName, topicName, onUndo }) => ({
      title: `${inheritedParentName} members are no longer inherited`,
      message: `Some ${inheritedParentName} members may still be members of ${topicName} directly or through a group.`,
      button: {
        text: 'Undo',
        onClick: onUndo,
      },
    }),
    {
      inheritedParentName: 'Topic 2',
      topicName: 'Topic 1',
      onUndo: () => {},
    },
  ),
  stillOwnerThroughGroupsWarning: template(
    ({ firstName, groupNames }) => ({
      level: 'warning',
      title: `${firstName} still has owner privileges`,
      message: `${firstName} is still an owner through groups ${groupNames}.`,
    }),
    { firstName: 'Jon', groupNames: 'Group 1, Group 2' },
  ),
  stillOwnerThroughParentWarning: template(
    ({ firstName, inheritedParentName }) => ({
      level: 'warning',
      title: `${firstName} still has owner privileges`,
      message: `${firstName} is still an owner through the parent topic ${inheritedParentName}.`,
    }),
    { firstName: 'Jon', inheritedParentName: 'Topic 2' },
  ),
  noLongerMember: template(
    ({ firstName, topicName, previousRole }) => ({
      level: 'success',
      title: `${firstName} has been removed as a direct ${previousRole}`,
      message: `${firstName} is no longer a member of ${topicName}.`,
    }),
    {
      firstName: 'Jon',
      topicName: 'Topic 1',
      previousRole: 'owner' as 'owner' | 'member',
    },
  ),
  noLongerDirectMemberButParentTopicWarning: template(
    ({ firstName, topicName, accessLevel, parentTopicName, previousRole }) => ({
      level: 'warning',
      title: `${firstName} has been removed as a direct ${previousRole}`,
      message: `${firstName} is no longer a direct member of ${topicName}
                    but is still an ${accessLevel} through the parent topic ${parentTopicName}.`,
    }),
    {
      firstName: 'Jon',
      topicName: 'Topic 1',
      accessLevel: 'member' as 'owner' | 'member',
      parentTopicName: 'Topic 2',
      previousRole: 'owner' as 'owner' | 'member',
    },
  ),
  noLongerDirectMemberButGroupWarning: template(
    ({ firstName, topicName, accessLevel, groupNames, previousRole }) => ({
      level: 'warning',
      title: `${firstName} has been removed as a direct ${previousRole}`,
      message: `${firstName} is no longer a direct member of ${
        topicName
      } but is still an ${accessLevel} through the group ${groupNames}.`,
    }),
    {
      firstName: 'Jon',
      topicName: 'Topic 1',
      accessLevel: 'member' as 'owner' | 'member',
      groupNames: 'Group 1, Group 2',
      previousRole: 'owner' as 'owner' | 'member',
    },
  ),
});

export const groupToasts = toastNamespace('Group', {
  accessGranted: template(
    ({ userName }) => ({
      level: 'success',
      message: `${userName} has been added as a member of this group.`,
    }),
    {
      userName: 'Jon',
    },
  ),
  memberAdded: template(
    ({ userName }) => ({
      level: 'success',
      message: `${userName} was added as a member.`,
    }),
    { userName: 'Jon' },
  ),
  subgroupAdded: template(
    ({ groupName }) => ({
      level: 'success',
      message: `${groupName} was added as a subgroup.`,
    }),
    { groupName: 'Group 1' },
  ),
  circularGroupError: toast({
    level: 'error',
    message: 'Cannot create circular group membership.',
  }),
  membershipRequested: toast({
    level: 'success',
    title: 'Membership requested',
    message:
      "Your request has been sent to the group owner. You will receive an email after you've been accepted.",
  }),
  youWereAddedAsOwner: toast({
    level: 'success',
    message: 'You were added as an owner.',
  }),
  youHaveBeenRemoved: toast({
    level: 'warning',
    message: 'You have been removed from the group.',
  }),
  userRemoved: template(
    ({ userName }) => ({
      level: 'success',
      message: `${userName} has been removed from the group.`,
    }),
    {
      userName: 'Jon',
    },
  ),
  subgroupRemoved: template(
    ({ groupName }) => ({
      level: 'success',
      message: `${groupName} has been removed from the group.`,
    }),
    { groupName: 'Group 1' },
  ),
  userPromotedToOwner: template(
    ({ userName }) => ({
      level: 'success',
      message: `${userName} has been promoted to an owner.`,
    }),
    { userName: 'Jon' },
  ),
  userDemotedToMember: template(
    ({ userName }) => ({
      level: 'success',
      message: `${userName} has been demoted to a standard member.`,
    }),
    { userName: 'Jon' },
  ),
  requestRejectedError: toast({
    level: 'error',
    message: 'Request has been rejected by another owner of this group.',
  }),
  requestDeclined: toast({
    level: 'success',
    message: 'Membership request declined.',
  }),
  noPermissionToCreateGroupsError: toast({
    level: 'error',
    title: "You don't have permission to create groups",
    message: 'Please contact an admin to request one.',
  }),
  groupCreationRequested: toast({
    level: 'success',
    message: 'Your group has been requested to the admins.',
  }),
});

export const accountToasts = toastNamespace('Account', {
  missingIntegrationError: template(
    ({ platform }) => ({
      level: 'error',
      message: `${platform} is not integrated.`,
    }),
    { platform: 'GitHub' },
  ),
  missingMembershipError: template(
    ({ platform }) => ({
      level: 'error',
      message: `That user account does not belong to the connected ${platform} account.`,
    }),
    { platform: 'GitHub' },
  ),
  seatLimitMaxedError: toast({
    level: 'error',
    title: 'Your team has reached its seat limit',
    message: 'Please upgrade or contact support.',
    button: ({ onDismiss }) => <ContactSupportButton onDismiss={onDismiss} />,
  }),
  userCreationDisallowedError: toast({
    level: 'error',
    message:
      'Your team requires each new member to be invited into Slab. Please ask one of your teammates to invite you.',
  }),
  deactivatedError: toast({
    level: 'error',
    message: 'This account has been deactivated.',
  }),
  unknownLoginError: toast({
    level: 'error',
    title: 'Unknown login error',
    message: 'Please contact support.',
    button: ({ onDismiss }) => <ContactSupportButton onDismiss={onDismiss} />,
  }),
  couldNotConnectToPlatformError: template(
    ({ platform }) => ({
      level: 'error',
      message: `Could not connect to ${platform}.`,
    }),
    { platform: 'GitHub' },
  ),
});

export const adminToasts = toastNamespace('Admin', {
  customDomainAdded: toast({
    level: 'success',
    title: 'Custom domain added',
    message:
      'It may take a several minutes to verify DNS and setup SSL so please check back later.',
  }),
  pendingDnsAndSslWarning: toast({
    level: 'warning',
    message:
      'DNS verification and SSL setup is still pending. Please check again later.',
  }),
  subscriptionUpdated: toast({
    level: 'success',
    message: 'Your subscription was updated successfully!',
  }),
  cardDeclinedError: toast({
    level: 'error',
    title: 'Card declined',
    message: 'Your card has been declined. Please use a different card.',
  }),
  unknownSubscriptionUpdateError: toast({
    level: 'error',
    title: 'Subscription update failed',
    message:
      'There was an error updating your subscription. Please refresh the page.',
  }),
  paymentSourceNotVerifiableError: toast({
    level: 'error',
    message: 'Could not verify payment source.',
  }),
  tokenCopied: toast({
    level: 'success',
    message: 'Token copied.',
  }),
  secretCopied: toast({
    level: 'success',
    message: 'Secret copied.',
  }),
  webhookUpdated: toast({
    level: 'success',
    message: 'Webhook successfully updated.',
  }),
  webhookCreated: toast({
    level: 'success',
    message: 'Webhook successfully created.',
  }),
  importError: toast({
    level: 'error',
    title: 'Unable to import your data',
    message: 'An error occurred while uploading. Please try again later.',
  }),
  massExportProcessed: toast({
    level: 'success',
    title: 'Your export is being processed',
    message:
      'You will receive an email with a download link once complete. This may take several minutes.',
  }),
  integrationEnabled: template(
    ({ platform }) => ({
      level: 'success',
      message: `${platform} has been integrated.`,
    }),
    { platform: 'GitHub' },
  ),
  genericIntegrationError: template(
    ({ platform }) => ({
      level: 'error',
      message: `Could not integrate ${platform}.`,
    }),
    { platform: 'GitHub' },
  ),
  specificIntegrationError: template(
    ({ platform, message }) => ({
      level: 'error',
      title: `Could not integrate ${platform}`,
      message: addPunctuationIfNeeded(message),
    }),
    {
      platform: 'GitHub',
      message: '<This is a placeholder for a platform specific error message>',
    },
  ),
  samlIntegrationError: template(
    ({ platform }) => ({
      level: 'error',
      title: `${platform} integration failed`,
      message: `There was an error enabling your ${platform} integration. Please contact support.`,
      button: ({ onDismiss }) => <ContactSupportButton onDismiss={onDismiss} />,
    }),
    {
      platform: 'GitHub',
    },
  ),
  invalidIntegrationTokenError: template(
    ({ platform }) => ({
      level: 'error',
      message: `Please enter a valid ${platform} API key.`,
    }),
    { platform: 'GitHub' },
  ),
  integrationUpdated: template(
    ({ platformName }) => ({
      level: 'success',
      title: `${platformName} integration updated`,
      message: `Your ${platformName} integration settings have been successfully saved.`,
    }),
    { platformName: 'GitHub' },
  ),
  integrationSyncEnabled: template(
    ({ platformName }) => ({
      level: 'success',
      title: `${platformName} integration updated`,
      message:
        'Posts from your repos are now being continuously synced. It may take some time to finish the initial sync.',
    }),
    { platformName: 'GitHub' },
  ),
  integrationRemoved: template(
    ({ platformName }) => ({
      level: 'success',
      message: `${platformName} integration removed.`,
    }),
    { platformName: 'GitHub' },
  ),
  integrationRequestSuccess: toast({
    level: 'success',
    message: 'Integration requested.',
  }),
  integrationRequestError: toast({
    level: 'error',
    message: 'Could not request integration.',
  }),
  provisionValidationError: toast({
    level: 'error',
    message: 'You need at least one standard user provision.',
  }),
  uploadedImageNotSupported: toast({
    level: 'error',
    message:
      'Uploaded image was not supported, please try again with a different file.',
  }),
  userRestoredAsAdmin: template(
    ({ onUndo, userName }) => ({
      level: 'success',
      title: 'User restored',
      message: `${userName} has been restored as an admin.`,
      button: onUndo
        ? {
            text: 'Undo',
            onClick: onUndo,
          }
        : undefined,
    }),
    {
      userName: 'Jon',
      onUndo: (() => {}) as (() => void) | undefined,
    },
  ),
  userRestoredAsGuest: template(
    ({ onUndo, userName }) => ({
      level: 'success',
      title: 'User restored',
      message: `${userName} has been restored as a guest.`,
      button: onUndo
        ? {
            text: 'Undo',
            onClick: onUndo,
          }
        : undefined,
    }),
    {
      userName: 'Jon',
      onUndo: (() => {}) as (() => void) | undefined,
    },
  ),
  userRestoredAsStandardUser: template(
    ({ onUndo, userName }) => ({
      level: 'success',
      title: 'User restored',
      message: `${userName} has been restored as a standard user.`,
      button: onUndo
        ? {
            text: 'Undo',
            onClick: onUndo,
          }
        : undefined,
    }),
    {
      userName: 'Jon',
      onUndo: (() => {}) as (() => void) | undefined,
    },
  ),
  userDeactivated: template(
    ({ onReactivate, userName, newOwnerName }) => ({
      level: 'success',
      title: 'User deactivated',
      message: `${userName} has been deactivated and owned posts have been transferred to ${newOwnerName}.`,
      button: onReactivate
        ? {
            text: 'Reactivate',
            onClick: onReactivate,
          }
        : undefined,
    }),
    {
      userName: 'Jon',
      newOwnerName: 'Sansa',
      onReactivate: (() => {}) as (() => void) | undefined,
    },
  ),
  userTypePromotedToAdmin: template(
    ({ onUndo, userName }) => ({
      level: 'success',
      title: 'Promoted to admin',
      message: `${userName} is now an admin.`,
      button: onUndo
        ? {
            text: 'Undo',
            onClick: onUndo,
          }
        : undefined,
    }),
    {
      userName: 'Jon',
      onUndo: (() => {}) as (() => void) | undefined,
    },
  ),
  userTypeDowngradedToGuest: template(
    ({ onUndo, userName }) => ({
      level: 'success',
      title: 'Downgraded to guest',
      message: `${userName} is now a guest.`,
      button: onUndo
        ? {
            text: 'Undo',
            onClick: onUndo,
          }
        : undefined,
    }),
    {
      userName: 'Jon',
      onUndo: (() => {}) as (() => void) | undefined,
    },
  ),
  userTypeUpdatedToStandardUser: template(
    ({ onUndo, userName }) => ({
      level: 'success',
      title: 'Updated to standard user',
      message: `${userName} is now a standard user.`,
      button: onUndo
        ? {
            text: 'Undo',
            onClick: onUndo,
          }
        : undefined,
    }),
    {
      userName: 'Jon',
      onUndo: (() => {}) as (() => void) | undefined,
    },
  ),
  userTypeUpgradedToStandardUser: template(
    ({ onUndo, userName }) => ({
      level: 'success',
      title: 'Upgraded to standard user',
      message: `${userName} is now a standard user.`,
      button: onUndo
        ? {
            text: 'Undo',
            onClick: onUndo,
          }
        : undefined,
    }),
    {
      userName: 'Jon',
      onUndo: (() => {}) as (() => void) | undefined,
    },
  ),
  userTypeDemotedStandardUser: template(
    ({ onUndo, userName }) => ({
      level: 'success',
      title: 'Demoted to standard user',
      message: `${userName} is now a standard user.`,
      button: onUndo
        ? {
            text: 'Undo',
            onClick: onUndo,
          }
        : undefined,
    }),
    {
      userName: 'Jon',
      onUndo: (() => {}) as (() => void) | undefined,
    },
  ),
  postOwnershipTransferred: template(
    ({ userName, newOwnerName }) => ({
      level: 'success',
      message: `Ownership of ${userName}'s published posts has been transferred to ${newOwnerName}.`,
    }),
    {
      userName: 'Jon',
      newOwnerName: 'Sansa',
    },
  ),
});

export const miscToasts = toastNamespace('Miscellaneous', {
  genericError: toast({
    level: 'error',
    title: 'Something went wrong',
    message: 'Please try again or contact support.',
    button: ({ onDismiss }) => <ContactSupportButton onDismiss={onDismiss} />,
  }),
  linkCopied: toast({
    level: 'success',
    message: 'Link copied.',
  }),
  shareableLinkCopied: toast({
    level: 'success',
    message: 'Shareable link copied.',
  }),
  messagingLoadError: toast({
    title: 'Cannot load messaging',
    message: 'Unable to load messaging, possibly due to Adblock.',
    level: 'error',
    button: {
      to: `https://go.slab.com/slab-contact-us`,
      text: 'Use support form',
      newTab: true,
    },
  }),
  openInDesktopApp: template(
    ({ to }) => ({
      title: 'Open in desktop app?',
      message:
        "You can view this post in Slab's native desktop app. Would you like to open this there?",
      button: {
        to,
        text: 'Open desktop app',
      },
    }),
    {
      to: 'https://slab.com/downloads',
    },
  ),
  redirectOnNotFoundError: template(
    ({ noun }) => ({
      level: 'error',
      message: `${_.capitalize(noun)} not found.`,
    }),
    { noun: 'topic' },
  ),
  noPermissionError: template(
    ({ noun }) => ({
      level: 'error',
      message: `You do not have permission to view this ${noun}.`,
    }),
    { noun: 'topic' },
  ),
  mustBeAdminWarning: toast({
    level: 'warning',
    message: 'Must be an admin to do that.',
  }),
  verificationEmailSent: toast({
    level: 'success',
    message: 'Verification email sent.',
  }),
  platformRemovedFromSearchResults: template(
    ({ platformName }) => ({
      level: 'success',
      message: `${platformName} has been removed from your search results.`,
    }),
    {
      platformName: 'Slack',
    },
  ),
  anyError: template(
    ({ message }) => ({
      level: 'error',
      message: addPunctuationIfNeeded(message),
    }),
    { message: 'An error occurred.' },
  ),
  anySuccess: template(
    ({ message }) => ({
      level: 'success',
      message: addPunctuationIfNeeded(message),
    }),
    { message: 'Success.' },
  ),
});
